import React, { createContext, useContext, useCallback, useMemo, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getTranslation } from '../i18n';
import { languageFromPath, localizePath, stripLocale } from '../utils/localePath';

const LanguageContext = createContext();

const TITLES = {
  de: 'Fox Performance | Amazon PPC aus Deutschland',
  en: 'Fox Performance | Amazon PPC from Germany',
};

export const LanguageProvider = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const language = languageFromPath(location.pathname);

  useEffect(() => {
    document.documentElement.lang = language;
    if (location.pathname.startsWith('/admin')) return;
    document.title = TITLES[language];
  }, [language, location.pathname]);

  const setLanguage = useCallback((lang) => {
    const nextLang = lang === 'en' ? 'en' : 'de';
    if (location.pathname.startsWith('/admin')) return;
    const next = localizePath(stripLocale(location.pathname), nextLang) + location.search + location.hash;
    const current = location.pathname + location.search + location.hash;
    if (next !== current) navigate(next);
  }, [location.pathname, location.search, location.hash, navigate]);

  const toPath = useCallback((path) => localizePath(path, language), [language]);

  const t = useMemo(() => getTranslation(language), [language]);

  const value = useMemo(
    () => ({ language, setLanguage, t, toPath }),
    [language, setLanguage, t, toPath]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};

export default LanguageContext;
